// QBO Account Mapping Wizard.
//
// Sidebar entry: Settings → QBO Mapping. The page Kevin will pull up
// when Clay (Ready.net) asks "where do the actuals on the Reimbursement
// page come from?"
//
// Layout (3 columns):
//   ┌─ UNMAPPED ─────┐  ┌─── 8 BEAD CATEGORIES ───┐  ┌── EXCLUDED ──┐
//   │ drag from here │  │  drop targets, 2×4 grid │  │ overhead etc │
//   └────────────────┘  └─────────────────────────┘  └──────────────┘
//
// Native HTML5 drag-drop: each account card has draggable=true and
// fires onDragStart with its id. Each drop bucket calls a mutation that
// PUTs /api/bead-pm/qbo-accounts/:id with { bead_category }. React Query
// invalidates → list refetches → card moves to its new bucket.

import { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';

type QboAccount = {
  id: string;
  qbo_account_code: string;
  qbo_account_name: string;
  account_type: string | null;
  bead_category: string;
  ytd_balance_usd: number;
  mapped_at: string | null;
  notes: string | null;
};

const BEAD_CATEGORIES: { v: string; l: string; pct: number; color: string; soft: string }[] = [
  { v: 'aerial_fiber',      l: 'Aerial Fiber',          pct: 18, color: '#3b82f6', soft: '#eff6ff' },
  { v: 'buried_fiber',      l: 'Buried Fiber',          pct: 36, color: '#10b981', soft: '#ecfdf5' },
  { v: 'network_equipment', l: 'Network Equipment',     pct: 10, color: '#8b5cf6', soft: '#f5f3ff' },
  { v: 'drops_cpe',         l: 'Drops & CPE',           pct: 14, color: '#f59e0b', soft: '#fffbeb' },
  { v: 'engineering',       l: 'Engineering & Design',  pct: 7,  color: '#ec4899', soft: '#fdf2f8' },
  { v: 'permits_row',       l: 'Permits / ROW',         pct: 6,  color: '#14b8a6', soft: '#f0fdfa' },
  { v: 'project_mgmt',      l: 'Project Management',    pct: 4,  color: '#6366f1', soft: '#eef2ff' },
  { v: 'contingency',       l: 'Contingency',           pct: 5,  color: '#64748b', soft: '#f8fafc' },
];

const fmtUSD = (n: number) => '$' + Math.round(n).toLocaleString();

function AccountCard({
  acc, onDragStart, onSendBack, isDragging,
}: {
  acc: QboAccount;
  onDragStart: (id: string) => void;
  onSendBack?: () => void;
  isDragging?: boolean;
}) {
  return (
    <div
      draggable
      onDragStart={(e) => {
        e.dataTransfer.setData('text/plain', acc.id);
        e.dataTransfer.effectAllowed = 'move';
        onDragStart(acc.id);
      }}
      style={{
        padding: '7px 10px',
        background: isDragging ? '#dbeafe' : 'white',
        border: '1px solid #e2e8f0',
        borderRadius: 4,
        marginBottom: 4,
        cursor: 'grab',
        userSelect: 'none',
        opacity: isDragging ? 0.4 : 1,
        transition: 'background 0.15s, opacity 0.15s',
        display: 'flex', alignItems: 'center', gap: 8,
      }}>
      <div style={{ fontFamily: 'ui-monospace, monospace', fontSize: 11, color: '#94a3b8', flexShrink: 0 }}>
        {acc.qbo_account_code}
      </div>
      <div style={{
        fontSize: 12, color: '#0f172a', flex: 1, minWidth: 0,
        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
      }}>
        {acc.qbo_account_name}
      </div>
      {acc.ytd_balance_usd > 0 && (
        <div style={{ fontSize: 11, color: '#475569', fontVariantNumeric: 'tabular-nums', flexShrink: 0 }}>
          {fmtUSD(acc.ytd_balance_usd)}
        </div>
      )}
      {onSendBack && (
        <button onClick={(e) => { e.stopPropagation(); onSendBack(); }}
          title="Send back to unmapped"
          style={{
            background: 'transparent', border: 0, color: '#94a3b8',
            cursor: 'pointer', fontSize: 12, padding: 0,
          }}>×</button>
      )}
    </div>
  );
}

function DropBucket({
  title, color, soft, subtitle, accounts,
  isOver, onDragOver, onDragLeave, onDrop,
  onDragStart, onSendBack,
  emptyHint,
}: {
  title: string;
  color: string;
  soft: string;
  subtitle?: string;
  accounts: QboAccount[];
  isOver: boolean;
  onDragOver: (e: React.DragEvent) => void;
  onDragLeave: () => void;
  onDrop: (e: React.DragEvent) => void;
  onDragStart: (id: string) => void;
  onSendBack?: (id: string) => void;
  emptyHint?: string;
}) {
  const total = accounts.reduce((s, a) => s + (Number(a.ytd_balance_usd) || 0), 0);
  return (
    <div
      onDrop={onDrop}
      onDragOver={(e) => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; onDragOver(e); }}
      onDragLeave={onDragLeave}
      style={{
        padding: 10,
        border: isOver ? `2px dashed ${color}` : '2px dashed #e2e8f0',
        borderRadius: 6,
        background: isOver ? soft : 'white',
        minHeight: 80,
        transition: 'all 0.15s',
      }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{ width: 10, height: 10, background: color, borderRadius: 2 }} />
          <span style={{ fontSize: 12, fontWeight: 700, color: '#0f172a' }}>{title}</span>
        </div>
        <div style={{ fontSize: 10, color: '#64748b' }}>
          {subtitle && <span>{subtitle} · </span>}
          {accounts.length} acct{accounts.length === 1 ? '' : 's'}
          {total > 0 && <> · {fmtUSD(total)}</>}
        </div>
      </div>
      {accounts.length === 0 && emptyHint && (
        <div style={{ fontSize: 11, color: '#94a3b8', fontStyle: 'italic', textAlign: 'center', padding: 8 }}>
          {emptyHint}
        </div>
      )}
      {accounts.map(acc => (
        <AccountCard
          key={acc.id}
          acc={acc}
          onDragStart={onDragStart}
          onSendBack={onSendBack ? () => onSendBack(acc.id) : undefined}
        />
      ))}
    </div>
  );
}

function KpiCard({ label, value, sub, tone }: { label: string; value: string; sub?: string; tone?: string }) {
  const color =
    tone === 'good' ? '#10b981' :
    tone === 'warn' ? '#f59e0b' :
    tone === 'bad'  ? '#ef4444' : 'var(--text)';
  return (
    <div className="card">
      <div className="card__body" style={{ padding: 16 }}>
        <div style={{ fontSize: 11, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '.05em' }}>{label}</div>
        <div style={{ fontSize: 24, fontWeight: 700, color, marginTop: 4 }}>{value}</div>
        {sub && <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>{sub}</div>}
      </div>
    </div>
  );
}

export default function QBOMappingPage() {
  const queryClient = useQueryClient();
  const [draggedId, setDraggedId] = useState<string | null>(null);
  const [overTarget, setOverTarget] = useState<string | null>(null);

  const { data: accounts = [], isLoading } = useQuery<QboAccount[]>({
    queryKey: ['qbo-accounts'],
    queryFn: () => api.get('/bead-pm/qbo-accounts'),
  });

  const update = useMutation({
    mutationFn: ({ id, category }: { id: string; category: string }) =>
      api.put(`/bead-pm/qbo-accounts/${id}`, { bead_category: category }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['qbo-accounts'] }),
  });

  const grouped = useMemo(() => {
    const m: Record<string, QboAccount[]> = {};
    for (const a of accounts) {
      (m[a.bead_category] ??= []).push(a);
    }
    return m;
  }, [accounts]);

  const mapped = accounts.filter(a => a.bead_category !== 'unmapped').length;
  const total = accounts.length;
  const mappedPct = total > 0 ? Math.round((mapped / total) * 100) : 0;
  const ytdMapped = accounts
    .filter(a => !['unmapped', 'excluded'].includes(a.bead_category))
    .reduce((s, a) => s + (Number(a.ytd_balance_usd) || 0), 0);

  const handleDragStart = (id: string) => setDraggedId(id);
  const handleDragEnd = () => { setDraggedId(null); setOverTarget(null); };

  const handleDrop = (target: string) => (e: React.DragEvent) => {
    e.preventDefault();
    const id = e.dataTransfer.getData('text/plain') || draggedId;
    if (!id) return;
    update.mutate({ id, category: target });
    handleDragEnd();
  };

  const handleSendBack = (id: string) => {
    update.mutate({ id, category: 'unmapped' });
  };

  return (
    <div onDragEnd={handleDragEnd}>
      <div className="page__header">
        <div>
          <h1 className="page__title">QBO Account Mapping</h1>
          <div className="page__subtitle">
            Map your QuickBooks chart-of-accounts to BEAD budget categories. Once mapped, every draw packet writes itself from live QBO data.
          </div>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginBottom: 16 }}>
        <KpiCard label="Accounts mapped" value={`${mapped} of ${total}`} sub={`${mappedPct}% complete`} tone={mappedPct >= 80 ? 'good' : mappedPct >= 50 ? 'warn' : 'bad'} />
        <KpiCard label="YTD on BEAD-eligible" value={fmtUSD(ytdMapped)} sub="Sum of mapped account balances" />
        <KpiCard label="Categories with data" value={`${BEAD_CATEGORIES.filter(c => (grouped[c.v] ?? []).length > 0).length} of 8`} />
        <KpiCard label="Excluded (overhead)" value={String((grouped.excluded ?? []).length)} sub="Not BEAD-eligible" />
      </div>

      {/* Helper banner */}
      <div className="card" style={{ marginBottom: 16 }}>
        <div className="card__body" style={{ padding: 14, display: 'flex', gap: 12, alignItems: 'flex-start' }}>
          <div style={{ fontSize: 22, lineHeight: 1, padding: '4px 8px', borderRadius: 6, background: 'rgba(245,158,11,0.12)', color: '#d97706' }}>★</div>
          <div style={{ flex: 1, fontSize: 13, color: '#475569', lineHeight: 1.55 }}>
            <b style={{ color: '#0f172a' }}>How this works.</b> Drag any unmapped QBO account into one of the 8 BEAD budget categories on the right.
            Once an account is mapped, its actuals flow into the matching category on the Reimbursements page automatically.
            Click the × on a mapped card to send it back to Unmapped.
          </div>
        </div>
      </div>

      {/* Main 2-column layout: Unmapped on left, BEAD categories on right */}
      <div style={{ display: 'grid', gridTemplateColumns: '320px 1fr', gap: 12 }}>
        {/* Left: Unmapped column (sticky) */}
        <div style={{ position: 'sticky', top: 8, alignSelf: 'flex-start' }}>
          <DropBucket
            title="Unmapped"
            color="#cbd5e1"
            soft="#f1f5f9"
            subtitle="drag from here →"
            accounts={grouped.unmapped ?? []}
            isOver={overTarget === 'unmapped'}
            onDragOver={() => setOverTarget('unmapped')}
            onDragLeave={() => setOverTarget(null)}
            onDrop={handleDrop('unmapped')}
            onDragStart={handleDragStart}
            emptyHint={isLoading ? 'Loading…' : '✓ All accounts mapped'}
          />
        </div>

        {/* Right: 8 BEAD categories in 2×4 grid + Excluded full-width below */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 8 }}>
            {BEAD_CATEGORIES.map(cat => (
              <DropBucket
                key={cat.v}
                title={cat.l}
                color={cat.color}
                soft={cat.soft}
                subtitle={`${cat.pct}%`}
                accounts={grouped[cat.v] ?? []}
                isOver={overTarget === cat.v}
                onDragOver={() => setOverTarget(cat.v)}
                onDragLeave={() => setOverTarget(null)}
                onDrop={handleDrop(cat.v)}
                onDragStart={handleDragStart}
                onSendBack={handleSendBack}
                emptyHint="(drop QBO accounts here)"
              />
            ))}
          </div>
          {/* Excluded bucket (full-width) */}
          <DropBucket
            title="Not BEAD-Eligible (overhead)"
            color="#9ca3af"
            soft="#f3f4f6"
            subtitle="excluded from draws"
            accounts={grouped.excluded ?? []}
            isOver={overTarget === 'excluded'}
            onDragOver={() => setOverTarget('excluded')}
            onDragLeave={() => setOverTarget(null)}
            onDrop={handleDrop('excluded')}
            onDragStart={handleDragStart}
            onSendBack={handleSendBack}
            emptyHint="(drop overhead / non-construction accounts here)"
          />
        </div>
      </div>
    </div>
  );
}
